import { useCallback } from "react"

import {
  transport,
  useTransportQuery,
  useTransportQueryUtils,
} from "@/transport"
import { useQueryClient, type QueryClient } from "@tanstack/react-query"

import type { GetReviewDisagreementStateRequest } from "@/types_api/backend/transport/get_review_disagreement_state_request"
import type {
  GetReviewDisagreementStateResponse,
  ReviewDisagreementProjectStateResource,
} from "@/types_api/backend/transport/get_review_disagreement_state_response"

export interface ReviewDisagreementProjectState {
  projectId: string
  unresolvedSimulationCount: number
  unresolvedUserSimulationCount: number
  unresolvedSimulationIds: string[]
  unresolvedUserSimulationIds: string[]
  unresolvedAnnotationIds: string[]
}

interface ReviewDisagreementState {
  projects: ReviewDisagreementProjectState[]
  projectsById: Record<string, ReviewDisagreementProjectState>
}

const mapReviewDisagreementProjectState = (
  project: ReviewDisagreementProjectStateResource,
): ReviewDisagreementProjectState => ({
  projectId: project.project_id,
  unresolvedSimulationCount: project.unresolved_simulation_count ?? 0,
  unresolvedUserSimulationCount: project.unresolved_user_simulation_count ?? 0,
  unresolvedSimulationIds: project.unresolved_simulation_ids ?? [],
  unresolvedUserSimulationIds: project.unresolved_user_simulation_ids ?? [],
  unresolvedAnnotationIds: project.unresolved_annotation_ids ?? [],
})

export function mapReviewDisagreementStateResponse(
  response: GetReviewDisagreementStateResponse,
): ReviewDisagreementState {
  const projects = response.projects.map(mapReviewDisagreementProjectState)
  return {
    projects,
    projectsById: Object.fromEntries(
      projects.map((project) => [project.projectId, project]),
    ),
  }
}

export function useReviewDisagreementStateTransport({
  projectIds,
  selectedProjectId,
  includeUnresolvedIds = false,
  enabled = true,
}: {
  projectIds: string[]
  selectedProjectId?: string | null
  includeUnresolvedIds?: boolean
  enabled?: boolean
}) {
  const hasProjectScope = projectIds.length > 0
  const requestProjectIds: GetReviewDisagreementStateRequest["project_ids"] =
    hasProjectScope ? [projectIds[0], ...projectIds.slice(1)] : [""]

  const request: GetReviewDisagreementStateRequest = {
    project_ids: requestProjectIds,
    include_unresolved_ids: includeUnresolvedIds,
    ...(selectedProjectId !== undefined
      ? { selected_project_id: selectedProjectId }
      : {}),
  }

  return useTransportQuery({
    operation: transport.operations.getReviewDisagreementState,
    request,
    options: {
      enabled: hasProjectScope && enabled,
      select: mapReviewDisagreementStateResponse,
    },
  })
}

const DISAGREEMENT_STATE_OPERATION =
  transport.operations.getReviewDisagreementState

const unique = (values: string[]) => [...new Set(values)].sort()

/**
 * Replace one conversation's entries in a full disagreement-state response with a recheck of
 * that conversation. Each conversation's state depends only on its own labels.
 */
export function mergeReviewDisagreementConversationState(
  current: GetReviewDisagreementStateResponse,
  recheck: GetReviewDisagreementStateResponse,
  simulationOutputId: string,
): GetReviewDisagreementStateResponse {
  const recheckById = new Map(
    recheck.projects.map((project) => [project.project_id, project]),
  )
  const replace = (existing: string[] = [], rechecked: string[] = []) =>
    unique([
      ...existing.filter((id) => id !== simulationOutputId),
      ...rechecked,
    ])

  return {
    ...current,
    projects: current.projects.map((project) => {
      const rechecked = recheckById.get(project.project_id)
      const unresolvedSimulationIds = replace(
        project.unresolved_simulation_ids,
        rechecked?.unresolved_simulation_ids,
      )
      const unresolvedUserSimulationIds = replace(
        project.unresolved_user_simulation_ids,
        rechecked?.unresolved_user_simulation_ids,
      )
      return {
        ...project,
        unresolved_simulation_count: unresolvedSimulationIds.length,
        unresolved_user_simulation_count: unresolvedUserSimulationIds.length,
        unresolved_simulation_ids: unresolvedSimulationIds,
        unresolved_user_simulation_ids: unresolvedUserSimulationIds,
        // Annotation ids do not name their conversation; stale ones stay until the next full read.
        unresolved_annotation_ids: unique([
          ...(project.unresolved_annotation_ids ?? []),
          ...(rechecked?.unresolved_annotation_ids ?? []),
        ]),
      }
    }),
  }
}

// Counts can be derived from the id lists only when the response carries every id.
const isMergeableRequest = (
  request: unknown,
): request is GetReviewDisagreementStateRequest => {
  if (request == null || typeof request !== "object") {
    return false
  }
  const candidate = request as GetReviewDisagreementStateRequest
  return (
    candidate.include_unresolved_ids === true &&
    candidate.selected_project_id == null &&
    candidate.simulation_output_ids == null
  )
}

// Latest recheck per read and conversation, shared by every caller of one query client.
const latestRecheckByClient = new WeakMap<QueryClient, Map<string, number>>()

const startRecheck = (queryClient: QueryClient, recheckKey: string) => {
  const latest =
    latestRecheckByClient.get(queryClient) ?? new Map<string, number>()
  latestRecheckByClient.set(queryClient, latest)
  const generation = (latest.get(recheckKey) ?? 0) + 1
  latest.set(recheckKey, generation)
  return () => latest.get(recheckKey) === generation
}

const requestIncludesProject = (request: unknown, projectId: string) => {
  const projectIds = (
    request as Partial<GetReviewDisagreementStateRequest> | null
  )?.project_ids
  return !Array.isArray(projectIds) || projectIds.includes(projectId)
}

/**
 * Refresh one conversation in every cached full disagreement-state read after a write, instead of
 * rereading whole projects. Inactive reads are included so another page's cache is current when it
 * remounts. With a project id, reads scoped to other projects are skipped and refresh when they next
 * go stale. A failed recheck that no newer one has replaced falls back to a full refetch.
 */
export function useReviewDisagreementConversationRecheck() {
  const queryClient = useQueryClient()
  const queryUtils = useTransportQueryUtils()

  return useCallback(
    async (simulationOutputId: string, projectId?: string | null) => {
      const operationKey = queryUtils
        .getKey(DISAGREEMENT_STATE_OPERATION)
        .slice(0, -1)
      const queries = queryClient
        .getQueryCache()
        .findAll({ queryKey: operationKey })

      await Promise.all(
        queries.map(async (query) => {
          const request = query.queryKey.at(-1)
          if (projectId && !requestIncludesProject(request, projectId)) {
            return
          }
          // A read without data has nothing to merge into; its next fetch must include the write.
          if (!isMergeableRequest(request) || query.state.data === undefined) {
            await queryClient.invalidateQueries({
              queryKey: query.queryKey,
              exact: true,
            })
            return
          }
          const isCurrent = startRecheck(
            queryClient,
            `${query.queryHash}\n${simulationOutputId}`,
          )
          const recheckRequest = {
            ...request,
            simulation_output_ids: [simulationOutputId],
          }
          try {
            // fetch() serves cached data while fresh; a recheck must always reach the server.
            queryClient.removeQueries({
              queryKey: queryUtils.getKey(
                DISAGREEMENT_STATE_OPERATION,
                recheckRequest,
              ),
              exact: true,
            })
            const recheck = await queryUtils.fetch(
              DISAGREEMENT_STATE_OPERATION,
              recheckRequest,
            )
            if (!isCurrent()) {
              return
            }
            queryClient.setQueryData<GetReviewDisagreementStateResponse>(
              query.queryKey,
              (current) =>
                current &&
                mergeReviewDisagreementConversationState(
                  current,
                  recheck,
                  simulationOutputId,
                ),
            )
          } catch {
            if (!isCurrent()) {
              return
            }
            await queryClient.invalidateQueries({
              queryKey: query.queryKey,
              exact: true,
            })
          }
        }),
      )
    },
    [queryClient, queryUtils],
  )
}
